import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import errorBoundary from '@ifeng-fe/errorBoundary';
import { formatPeopleCount } from '@src/public/utils/format';
import { getInteractionVotes, saveInteractionVotes } from '@src/public/utils/interactionStorage';
import { resolveArticleId } from '@src/public/services/interactionCounts';
import { Sheet } from '@src/mobile/components/ModalBox';
import { postInteractionAccumulate } from '../services';
import styles from './index.css';
import CommentModal from './CommentModal';
import auth, { LoginDialog } from '@ifeng-fe/ui_pc_auth';

const cx = classNames.bind(styles);

const likeConfigMap = new Map([
    [0, { keys: [], classes: [] }],
    [1, { keys: ['like'], classes: ['like'] }],
    [2, { keys: ['ding', 'cai'], classes: ['ding', 'cai'] }],
    [3, { keys: ['flower'], classes: ['flower'] }],
    [4, { keys: ['candle'], classes: ['candle'] }],
    [5, { keys: ['cheer'], classes: ['cheer'] }],
]);

const isDingCai = key => key === 'ding' || key === 'cai';

const isAccumulateSuccess = res => {
    const code = res?.code ?? res?.data?.code;

    return code === 1 || code === '1';
};

const Footer = ({ content, timeline, index }) => {
    const { interaction, likeConfig, id, allowComment, title, skey } = content;
    const { comment } = interaction || {};
    const { allowComment: timelineAllowComment } = timeline;
    const itemAllowComment = allowComment === 1;
    const finalAllowComment = timelineAllowComment && itemAllowComment;
    const articleId = content.articleId || resolveArticleId(content) || id;

    const likconfigData = likeConfigMap.get(likeConfig);
    const { keys: likconfigKeys, classes: likconfigClasses } = likconfigData;
    const localVotes = getInteractionVotes(articleId);
    const interactionInitState = likconfigKeys.map((key, index) => {
        const { count, choosen } = interaction?.[key] || {};
        const iconClass = likconfigClasses[index];
        const local = localVotes[key];
        const checked = local ? !!local.checked : !!choosen;

        return { key, count: count || 0, checked, iconClass };
    });
    const [interactionList, setInteractionList] = useState(interactionInitState);
    const [votedMap, setVotedMap] = useState(() => {
        const map = {};

        interactionInitState.forEach(item => {
            const local = localVotes[item.key];

            if (local?.voted || local?.checked || item.checked) {
                map[item.key] = true;
            }
        });

        return map;
    });
    const [lock, setLock] = useState(false);
    const remoteCountKey = likconfigKeys.map(key => interaction?.[key]?.count ?? 0).join('|');

    // TODO: 把所有的图标聚合到一个数组中
    const colLength = interactionList.length + 1;
    const colWidth = `${100 / colLength}%`;

    const [showCommentGuide, setShowCommentGuide] = useState(false);

    const closeCommentGuide = () => {
        if (showCommentGuide) {
            setShowCommentGuide(false);
        }
    };

    const [showComment, setShowComment] = useState(false);

    const handleComment = () => {
        if (finalAllowComment) {
            setShowComment(true);
            closeCommentGuide();
        }
    };

    const handleCloseComment = () => {
        setShowComment(false);
    };

    const loginIdRef = useRef(auth.uuid());

    useEffect(() => {
        auth.event.on(auth.EVENTNAMES.login, () => {
            window.location.reload();
        });
    }, []);

    useEffect(
        () => {
            if (index === 0) {
                try {
                    const localKey = 'timeline/hasShowCommentGuide';
                    const hasShowCommentGuide = !!window.localStorage.getItem(localKey);

                    if (!hasShowCommentGuide) {
                        setShowCommentGuide(true);
                        window.localStorage.setItem(localKey, '1');
                    }
                } catch (e) {
                    console.error(e);
                }
            }
        },
        [index],
    );

    // 批量接口回填后，同步远端计数（保留本地选中态）
    useEffect(
        () => {
            setInteractionList(prev =>
                prev.map(item => ({
                    ...item,
                    count: Number(interaction?.[item.key]?.count) || 0,
                })),
            );
        },
        [remoteCountKey],
    );

    const handleCommentGuideClick = e => {
        e.stopPropagation();
        closeCommentGuide();
    };

    const applyLocalToggle = (clickedKey, markVoted = false) => {
        setInteractionList(prevList => {
            const nextList = prevList.map(item => {
                if (item.key === clickedKey) {
                    if (item.checked) {
                        return {
                            ...item,
                            checked: false,
                            count: Math.max(item.count - 1, 0),
                        };
                    }

                    return {
                        ...item,
                        checked: true,
                        count: item.count + 1,
                    };
                }

                // ding / cai 互斥：选中一方时取消另一方
                if (isDingCai(clickedKey) && isDingCai(item.key) && item.checked) {
                    return {
                        ...item,
                        checked: false,
                        count: Math.max(item.count - 1, 0),
                    };
                }

                return item;
            });

            setVotedMap(prevVoted => {
                const nextVoted = markVoted ? { ...prevVoted, [clickedKey]: true } : prevVoted;

                saveInteractionVotes(articleId, nextList, nextVoted);

                return nextVoted;
            });

            return nextList;
        });
    };

    const handleInteractionClick = async key => {
        if (!auth.isLogin()) {
            auth.login(loginIdRef.current);

            return;
        }

        if (lock) {
            return;
        }

        const current = interactionList.find(item => item.key === key);

        if (!current) {
            return;
        }

        // 已请求过该类型：只改本地选中态与计数
        if (votedMap[key]) {
            applyLocalToggle(key);

            return;
        }

        // 取消选中且尚未记为已投票：仅本地切换（一般不会走到）
        if (current.checked) {
            applyLocalToggle(key);

            return;
        }

        setLock(true);

        try {
            const res = await postInteractionAccumulate({ type: key, articleId });

            if (!isAccumulateSuccess(res)) {
                return;
            }

            applyLocalToggle(key, true);
        } catch (e) {
            console.error(e);
        } finally {
            setLock(false);
        }
    };

    return (
        <div className={cx('wrap', { wrapSingle: colLength === 1 })}>
            {interactionList.map(item => {
                const { key, count, checked, iconClass } = item;

                return (
                    <div className={styles.col} key={key} onClick={() => handleInteractionClick(key)} style={{ width: colWidth }}>
                        <div className={cx('icon', iconClass, { checked })} />
                        <div>{count > 0 ? formatPeopleCount(count) : ''}</div>
                    </div>
                );
            })}
            <div
                className={cx('col', { colNotAllowComment: !finalAllowComment, colSingle: colLength === 1 })}
                style={{ width: colWidth }}
                onClick={handleComment}>
                {finalAllowComment && showCommentGuide ? (
                    <>
                        <div className={styles.commentGuide} onClick={handleCommentGuideClick}>
                            <div className={styles.commentGuideContent}>
                                <div className={styles.commentGuideText}>新功能开放，点击参与讨论哦~</div>
                                <div className={styles.commentGuideClose} />
                            </div>
                        </div>
                        <div className={styles.commentGuideArrow} onClick={handleCommentGuideClick} />
                    </>
                ) : null}
                <div className={cx('icon', 'comment')} />
                <div>{comment > 0 ? formatPeopleCount(comment) : '评论'}</div>
            </div>
            <Sheet top={80} show={showComment} onClose={handleCloseComment}>
                <CommentModal
                    subjectId={articleId}
                    close={handleCloseComment}
                    docUrl={`ucms_${articleId}`}
                    docName={title}
                    skey={skey}
                />
            </Sheet>
            <LoginDialog id={loginIdRef.current} />
        </div>
    );
};

Footer.propTypes = {
    timeline: PropTypes.object,
    content: PropTypes.object,
    index: PropTypes.number,
};

export default errorBoundary(Footer);
